import React, { useState } from "react";
import { useNavigate } from "react-router-dom";
import { changePassword, deleteAccount } from "../api/authClient";
import { useAuth } from "../contexts/AuthContext";
import TwoFactorSettings from "./security/TwoFactorSettings";
import SessionManagement from "./security/SessionManagement";
import DeviceManagement from "./security/DeviceManagement";
import SubscriptionSettings from "./SubscriptionSettings";

export default function SettingsPage() {
    const navigate = useNavigate();
    const { logout } = useAuth();

    // Dashboard preferences
    const [showEmergencyFund, setShowEmergencyFund] = useState(() => {
        const saved = localStorage.getItem("showEmergencyFund");
        return saved !== null ? JSON.parse(saved) : true;
    });

    const handleEmergencyFundToggle = () => {
        setShowEmergencyFund(prev => {
            const newValue = !prev;
            localStorage.setItem("showEmergencyFund", JSON.stringify(newValue));
            return newValue;
        });
    };

    // Password change state
    const [currentPassword, setCurrentPassword] = useState("");
    const [newPassword, setNewPassword] = useState("");
    const [confirmPassword, setConfirmPassword] = useState("");
    const [passwordError, setPasswordError] = useState("");
    const [passwordSuccess, setPasswordSuccess] = useState("");
    const [passwordLoading, setPasswordLoading] = useState(false);

    // Delete account state
    const [deletePassword, setDeletePassword] = useState("");
    const [deleteConfirmation, setDeleteConfirmation] = useState("");
    const [deleteError, setDeleteError] = useState("");
    const [deleteLoading, setDeleteLoading] = useState(false);
    const [showDeleteConfirm, setShowDeleteConfirm] = useState(false);

    // Password validation helper
    const validatePassword = (pwd) => {
        const errors = [];
        if (pwd.length < 8) errors.push("At least 8 characters.");
        if (!/[A-Z]/.test(pwd)) errors.push("At least one uppercase letter.");
        if (!/[a-z]/.test(pwd)) errors.push("At least one lowercase letter.");
        if (!/[0-9]/.test(pwd)) errors.push("At least one number.");
        if (!/[!@#$%^&*()_+\-=[\]{}|;:,.<>?]/.test(pwd)) errors.push("At least one special character.");
        return errors;
    };

    const handlePasswordChange = async (e) => {
        e.preventDefault();
        setPasswordError("");
        setPasswordSuccess("");

        if (newPassword !== confirmPassword) {
            setPasswordError("New passwords do not match");
            return;
        }

        const passwordErrors = validatePassword(newPassword);
        if (passwordErrors.length > 0) {
            setPasswordError(passwordErrors.join(" "));
            return;
        }

        setPasswordLoading(true);
        try {
            await changePassword(currentPassword, newPassword);
            setPasswordSuccess("Password changed successfully");
            setCurrentPassword("");
            setNewPassword("");
            setConfirmPassword("");
        } catch (err) {
            setPasswordError(err.response?.data?.message || "Failed to change password");
        } finally {
            setPasswordLoading(false);
        }
    };

    const handleDeleteAccount = async (e) => {
        e.preventDefault();
        setDeleteError("");

        if (deleteConfirmation !== "DELETE") {
            setDeleteError("Please type DELETE to confirm");
            return;
        }

        setDeleteLoading(true);
        try {
            await deleteAccount(deletePassword, deleteConfirmation);
            await logout();
            navigate("/", { replace: true });
        } catch (err) {
            setDeleteError(err.response?.data?.message || "Failed to delete account");
        } finally {
            setDeleteLoading(false);
        }
    };

    return (
        <div className="max-w-2xl mx-auto space-y-8">
            <div>
                <h1 className="text-xl sm:text-2xl font-bold text-gray-900">Settings</h1>
                <p className="text-gray-500 mt-1 text-sm sm:text-base">Manage your account settings</p>
            </div>

            {/* Subscription Section */}
            <SubscriptionSettings />

            {/* Dashboard Preferences */}
            <div className="card p-6">
                <h2 className="text-lg font-semibold text-gray-900 mb-4">Dashboard</h2>
                <p className="text-gray-500 text-sm mb-4">Customize what appears on your dashboard</p>

                <div className="flex items-center justify-between py-3">
                    <div>
                        <p className="text-sm font-medium text-gray-800">Emergency Fund Tracker</p>
                        <p className="text-xs text-gray-500">Show the emergency fund progress card</p>
                    </div>
                    <label className="relative inline-flex cursor-pointer">
                        <input
                            type="checkbox"
                            checked={showEmergencyFund}
                            onChange={handleEmergencyFundToggle}
                            className="sr-only peer"
                        />
                        <div className="w-11 h-6 bg-gray-200 rounded-full peer-checked:bg-accent-500 transition-colors" />
                        <div className="absolute top-0.5 left-0.5 w-5 h-5 bg-white rounded-full shadow transition-transform peer-checked:translate-x-5" />
                    </label>
                </div>
            </div>

            {/* Security Section */}
            <div className="space-y-6">
                <div>
                    <h2 className="text-xl font-bold text-gray-900 mb-1">Security</h2>
                    <p className="text-gray-500 text-sm">Manage your account security settings</p>
                </div>

                <TwoFactorSettings />
                <SessionManagement />
                <DeviceManagement />
            </div>

            {/* Password Change Section */}
            <div className="card p-6">
                <h2 className="text-lg font-semibold text-gray-900 mb-4">Change Password</h2>

                {passwordError && (
                    <div className="mb-4 p-3 bg-red-50 border border-red-200 rounded-lg">
                        <p className="text-sm text-danger-500">{passwordError}</p>
                    </div>
                )}
                {passwordSuccess && (
                    <div className="mb-4 p-3 bg-accent-50 border border-accent-200 rounded-lg">
                        <p className="text-sm text-accent-600">{passwordSuccess}</p>
                    </div>
                )}

                <form onSubmit={handlePasswordChange} className="space-y-4">
                    <div>
                        <label className="block text-sm font-medium text-gray-700 mb-2">
                            Current Password
                        </label>
                        <input
                            type="password"
                            value={currentPassword}
                            onChange={(e) => setCurrentPassword(e.target.value)}
                            required
                            className="input w-full"
                            placeholder="Enter current password"
                        />
                    </div>
                    <div>
                        <label className="block text-sm font-medium text-gray-700 mb-2">
                            New Password
                        </label>
                        <input
                            type="password"
                            value={newPassword}
                            onChange={(e) => setNewPassword(e.target.value)}
                            required
                            minLength={8}
                            className="input w-full"
                            placeholder="Create a strong password"
                        />
                        <p className="text-xs text-gray-500 mt-1">
                            Must be 8+ characters with uppercase, lowercase, number, and special character
                        </p>
                    </div>
                    <div>
                        <label className="block text-sm font-medium text-gray-700 mb-2">
                            Confirm New Password
                        </label>
                        <input
                            type="password"
                            value={confirmPassword}
                            onChange={(e) => setConfirmPassword(e.target.value)}
                            required
                            className="input w-full"
                            placeholder="Confirm new password"
                        />
                    </div>
                    <button
                        type="submit"
                        disabled={passwordLoading}
                        className="btn-primary"
                    >
                        {passwordLoading ? "Changing..." : "Change Password"}
                    </button>
                </form>
            </div>

            {/* Danger Zone - Delete Account */}
            <div className="card p-6 border-red-200">
                <h2 className="text-lg font-semibold text-danger-500 mb-2">Danger Zone</h2>
                <p className="text-gray-500 text-sm mb-4">
                    Once you delete your account, there is no going back. All your data will be permanently removed.
                </p>

                {!showDeleteConfirm ? (
                    <button
                        onClick={() => setShowDeleteConfirm(true)}
                        className="px-4 py-2 bg-red-50 border border-red-200 text-danger-500 rounded-lg hover:bg-red-100 transition-colors"
                    >
                        Delete Account
                    </button>
                ) : (
                    <div className="space-y-4 p-4 bg-red-50 rounded-lg border border-red-200">
                        <p className="text-sm text-gray-700">
                            This action cannot be undone. This will permanently delete your account and all associated data including:
                        </p>
                        <ul className="text-sm text-gray-500 list-disc list-inside space-y-1">
                            <li>All linked bank accounts</li>
                            <li>Transaction history</li>
                            <li>Net worth snapshots</li>
                            <li>Profile information</li>
                        </ul>

                        {deleteError && (
                            <div className="p-3 bg-red-50 border border-red-200 rounded-lg">
                                <p className="text-sm text-danger-500">{deleteError}</p>
                            </div>
                        )}

                        <form onSubmit={handleDeleteAccount} className="space-y-4">
                            <div>
                                <label className="block text-sm font-medium text-gray-700 mb-2">
                                    Enter your password
                                </label>
                                <input
                                    type="password"
                                    value={deletePassword}
                                    onChange={(e) => setDeletePassword(e.target.value)}
                                    required
                                    className="input w-full"
                                    placeholder="Your password"
                                />
                            </div>
                            <div>
                                <label className="block text-sm font-medium text-gray-700 mb-2">
                                    Type <span className="text-danger-500 font-mono">DELETE</span> to confirm
                                </label>
                                <input
                                    type="text"
                                    value={deleteConfirmation}
                                    onChange={(e) => setDeleteConfirmation(e.target.value)}
                                    required
                                    className="input w-full font-mono"
                                    placeholder="DELETE"
                                />
                            </div>
                            <div className="flex gap-3">
                                <button
                                    type="button"
                                    onClick={() => {
                                        setShowDeleteConfirm(false);
                                        setDeletePassword("");
                                        setDeleteConfirmation("");
                                        setDeleteError("");
                                    }}
                                    className="px-4 py-2 bg-gray-200 text-gray-700 rounded-lg hover:bg-gray-300 transition-colors"
                                >
                                    Cancel
                                </button>
                                <button
                                    type="submit"
                                    disabled={deleteLoading || deleteConfirmation !== "DELETE"}
                                    className="px-4 py-2 bg-danger-500 text-white rounded-lg hover:bg-danger-600 transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
                                >
                                    {deleteLoading ? "Deleting..." : "Permanently Delete Account"}
                                </button>
                            </div>
                        </form>
                    </div>
                )}
            </div>
        </div>
    );
}
